import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiGift, FiClock, FiCheckCircle, FiXCircle, FiTrendingUp } from 'react-icons/fi';
import api from '../services/api';

interface CashbackBalance {
  saldo_cashback: number;
  total_recebido: number;
  total_usado: number;
  total_expirado: number;
  pendente: number;
  moeda: string;
}

interface CashbackRecord {
  id: number;
  valor_transacao: number;
  valor_cashback: number;
  percentagem: number | null;
  estado: string;
  campaign_name: string | null;
  campaign_tipo: string | null;
  created_at: string;
  valid_until: string | null;
}

interface CashbackCampaign {
  id: number;
  name: string;
  description: string | null;
  tipo: string;
  percentagem: number | null;
  valor_fixo: number | null;
  max_por_transacao: number | null;
  start_date: string;
  end_date: string;
}

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', minimumFractionDigits: 0 }).format(amount);

const formatDate = (d: string): string => {
  try {
    return new Date(d).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return d;
  }
};

const estadoConfig: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
  CONFIRMADO: { color: 'text-green-600', bg: 'bg-green-100', icon: <FiCheckCircle /> },
  PENDENTE: { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: <FiClock /> },
  EXPIRADO: { color: 'text-red-500', bg: 'bg-red-100', icon: <FiXCircle /> },
  CANCELADO: { color: 'text-gray-500', bg: 'bg-gray-100', icon: <FiXCircle /> },
  UTILIZADO: { color: 'text-blue-600', bg: 'bg-blue-100', icon: <FiCheckCircle /> },
};

const Cashback: React.FC = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<CashbackBalance | null>(null);
  const [history, setHistory] = useState<CashbackRecord[]>([]);
  const [campaigns, setCampaigns] = useState<CashbackCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'history' | 'campaigns'>('history');

  useEffect(() => {
    const load = async () => {
      try {
        const [balRes, histRes, campRes] = await Promise.allSettled([
          api.get('/client/cashback/balance'),
          api.get('/client/cashback/history?limit=30'),
          api.get('/client/cashback/active-campaigns'),
        ]);
        if (balRes.status === 'fulfilled' && balRes.value.data?.data) setBalance(balRes.value.data.data);
        if (histRes.status === 'fulfilled' && histRes.value.data?.data) setHistory(histRes.value.data.data);
        if (campRes.status === 'fulfilled' && campRes.value.data?.data) setCampaigns(campRes.value.data.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-48 bg-gray-200 rounded-2xl" />
        <div className="h-64 bg-gray-200 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
          <FiArrowLeft className="text-xl" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Cashback</h1>
      </div>

      {/* Balance Card */}
      <div className="rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #6B4EFF 0%, #9B7DFF 100%)' }}>
        <p className="text-white/70 text-sm">Saldo Cashback</p>
        <p className="text-4xl font-bold mt-1">{formatAmount(balance?.saldo_cashback || 0)}</p>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div>
            <p className="text-white/60 text-xs">Recebido</p>
            <p className="font-semibold text-green-300">{formatAmount(balance?.total_recebido || 0)}</p>
          </div>
          <div>
            <p className="text-white/60 text-xs">Usado</p>
            <p className="font-semibold text-blue-300">{formatAmount(balance?.total_usado || 0)}</p>
          </div>
          <div>
            <p className="text-white/60 text-xs">Expirado</p>
            <p className="font-semibold text-red-300">{formatAmount(balance?.total_expirado || 0)}</p>
          </div>
        </div>

        {(balance?.pendente || 0) > 0 && (
          <p className="text-white/50 text-xs mt-3">Pendente: {formatAmount(balance!.pendente)}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            tab === 'history' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Historico
        </button>
        <button
          onClick={() => setTab('campaigns')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            tab === 'campaigns' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Campanhas Ativas ({campaigns.length})
        </button>
      </div>

      {/* History */}
      {tab === 'history' && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FiGift className="text-4xl mx-auto mb-3" />
              <p>Sem registos de cashback</p>
              <p className="text-sm mt-1">Faca pagamentos para receber cashback!</p>
            </div>
          ) : (
            history.map((r) => {
              const cfg = estadoConfig[r.estado] || estadoConfig.PENDENTE;
              return (
                <div key={r.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center ${cfg.color}`}>
                      {cfg.icon}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{r.campaign_name || 'Cashback'}</p>
                      <p className="text-xs text-gray-400">
                        {formatDate(r.created_at)}
                        {r.percentagem ? ` — ${r.percentagem}% de ${formatAmount(r.valor_transacao)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${cfg.color}`}>+{formatAmount(r.valor_cashback)}</p>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                      {r.estado}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Campaigns */}
      {tab === 'campaigns' && (
        <div className="space-y-3">
          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FiTrendingUp className="text-4xl mx-auto mb-3" />
              <p>Sem campanhas ativas de momento</p>
            </div>
          ) : (
            campaigns.map((c) => (
              <div key={c.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{c.name}</h3>
                    {c.description && <p className="text-sm text-gray-500 mt-1">{c.description}</p>}
                  </div>
                  <div className="ml-3 text-right">
                    <span className="inline-block px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-bold">
                      {c.tipo === 'PERCENTUAL' ? `${c.percentagem}%` : formatAmount(c.valor_fixo || 0)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                  <span>Ate {formatDate(c.end_date)}</span>
                  {c.max_por_transacao && <span>Max/transacao: {formatAmount(c.max_por_transacao)}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Cashback;
